import { observable, randomId } from '@aha-app/mvc';
import {
  type IDataStoreRecordBehaviors,
  type KeyPath,
} from '../collaboration/DataStoreRecordBehaviors';
import { type Constructor, type RecordId } from '../types';
import {
  insert,
  moveAfter,
  moveBefore,
  sortCollection,
} from '../utils/sparseCollectionUtils';
import type ItemPlacement from './ItemPlacement';
import { AFTER, BEFORE, BOTTOM, TOP } from './ItemPlacement';

// Key against which we sort items. Use 'item_' prefix to avoid conflict
const POSITION_KEY = 'item_position';

export interface SortableItem {
  id: RecordId;
  [POSITION_KEY]: number;
}

class ItemCollection<T extends SortableItem> {
  items: T[];

  constructor(
    public owner: IDataStoreRecordBehaviors,
    public key: string,
    public objectType: Constructor<T>
  ) {
    this.loadCollection();
  }

  get data(): Record<string, T> {
    return this.owner[this.dataKey] || {};
  }

  get dataKey() {
    return `${this.key}Data`;
  }

  createItem(data) {
    const ObjectClass = this.objectType;
    return observable(new ObjectClass(data));
  }

  loadCollection() {
    // Clean any null items that may not have been removed properly
    Object.keys(this.data).forEach(id => {
      if (!this.data[id]) {
        delete this.data[id];
      }
    });

    this.items = observable(
      Object.values(this.data).map(data => this.createItem(data))
    );
    sortCollection(this.items, POSITION_KEY);
  }

  add(values: Partial<Omit<T, 'id'>> = {}) {
    const { owner, key } = this;

    const id = randomId() as RecordId;
    const data = { id, ...values };
    const item = this.createItem(data);
    const position = insert(this.items, item, POSITION_KEY);
    item[POSITION_KEY] = position;
    data[POSITION_KEY] = position;
    owner[key][id] = item;

    owner.set([this.dataKey, id], data);

    return id;
  }

  remove(id: T['id']) {
    const { owner, key } = this;
    const index = this.items.findIndex(item => item.id === id);
    if (index > -1) {
      this.items.splice(index, 1);
    }

    owner[key][id] = null;
    owner.set([this.dataKey, id], null);
  }

  // Helper method for removing the last item in the collection, i.e., the item
  // with the highest position
  removeLast() {
    const last = this.items.reduce((max, current) => {
      return current[POSITION_KEY] > max[POSITION_KEY] ? current : max;
    });
    if (last) {
      this.remove(last.id);
    }
  }

  update(item: T, values: Partial<Omit<T, 'id'>> = {}) {
    Object.keys(values).forEach(key => {
      if (['id'].includes(key)) return;

      this.owner.set([this.dataKey, item.id, key], values[key]);
      item[key] = values[key];
    });
  }

  moveBefore(index, item) {
    moveBefore(this.items, item, index, POSITION_KEY);
    this.owner.set([this.dataKey, item.id, POSITION_KEY], item[POSITION_KEY]);
  }

  moveAfter(index, item) {
    moveAfter(this.items, item, index, POSITION_KEY);
    this.owner.set([this.dataKey, item.id, POSITION_KEY], item[POSITION_KEY]);
  }

  placeItem(newItem: T, placement: ItemPlacement) {
    // Copy array to avoid mutating the original
    const items = this.items.slice();

    // Get the item's new sequential index in the array
    const index = placement.indexForList(items);

    // Remove the dragged item from the list
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].id === newItem.id) {
        items.splice(i, 1);
      }
    }

    switch (placement.position) {
      case TOP:
        this.moveBefore(0, newItem);
        break;

      case BEFORE:
        this.moveBefore(index, newItem);
        break;

      case AFTER:
        this.moveAfter(index, newItem);
        break;

      case BOTTOM:
      default:
        this.moveAfter(this.items.length - 1, newItem);
        break;
    }
  }

  findById(id: RecordId): T | undefined {
    return this.items.find(item => item.id === id);
  }

  handleSetFromRemote(key: KeyPath, value: any): void {
    if (!Array.isArray(key) || key[0] !== this.dataKey || key.length < 2)
      return;

    if (key.length === 2 && value === null) {
      // Deleting the item
      const index = this.items.findIndex(item => item.id === key[1]);
      if (index > -1) {
        this.items.splice(index, 1);
      }
    } else if (key.length !== 3) {
      // Trying to set a value deeper than a single key on this object… not currently supported
      throw new Error('Cannot update value with depth greater than 3.');
    }

    let item = this.findById(key[1] as RecordId);
    if (!item) {
      // Creating new object
      if (value) {
        item = this.createItem(value);
        this.items.push(observable(item));
      }

      return;
    }

    item[key[2]] = value;
    // Resort the items if the index of one has changed
    if (key[2] === POSITION_KEY) {
      sortCollection(this.items, POSITION_KEY);
    }
  }
}

export default ItemCollection;
